import { useEffect, useRef, useState, type MutableRefObject, type ReactNode } from 'react';
import { ArrowDown, ArrowUp, FileDown, Loader2, Printer, Star } from 'lucide-react';
import { getStaffPass } from '@generated/hooks/staffPass';
import { getStarsReport } from '@generated/server/stars';
import { StarsSocial } from '@generated/components/StarsSocial';
import { StarsDaily } from '@generated/components/StarsDaily';
import { StarsBallot } from '@generated/components/StarsBallot';
import { exportPagesToPdf } from '@generated/components/pagedPdf';
import { RHSR_LOGO } from '@generated/components/AdGuide';

type Report = Awaited<ReturnType<typeof getStarsReport>>;
type Cat = Report['categories'][number];
const head = 'font-[family-name:var(--font-heading)]';
// Voting closes Nov 10, 2026 — until then the top pick is a "Leader", not a "Winner".
const isFinal = (r: Report) => new Date(r.generated) >= new Date(2026, 10, 11);

function Move({ m, pdf = false }: { m: 'up' | 'down' | null; pdf?: boolean }) {
  if (!m) return null;
  const cls = pdf ? 'ml-1 inline text-[12px] font-black' : 'ml-1.5 inline h-4 w-4 align-[-2px]';
  if (pdf) return m === 'up' ? <span className={`${cls} text-[hsl(var(--brand-green))]`} aria-label="moved up">▲</span> : <span className={`${cls} text-primary`} aria-label="moved down">▼</span>;
  return m === 'up' ? <ArrowUp aria-label="moved up" strokeWidth={3} className={`${cls} text-[hsl(var(--brand-green))]`} /> : <ArrowDown aria-label="moved down" strokeWidth={3} className={`${cls} text-primary`} />;
}

function Category({ c, final, big = false }: { c: Cat; final: boolean; big?: boolean }) {
  return (
    <section className="break-inside-avoid border-t-2 border-foreground pt-2">
      <div className="flex items-start justify-between gap-3"><h3 className={`${head} ${big ? "text-[15px] leading-[1.35]" : "text-lg leading-snug"} min-w-0 font-semibold`}>{c.label}</h3><span className="shrink-0 text-xs text-muted-foreground">{c.ballots} votes</span></div>
      {c.top.length === 0 ? <p className="text-sm text-muted-foreground">No votes yet.</p> : (
        <ol className={`${big ? "mt-1.5 space-y-1 text-[13px] leading-[1.35]" : "mt-1 space-y-0.5 text-sm"}`}>{c.top.map((t, i) => (
          <li key={t.name} className={`flex justify-between gap-3 ${i === 0 ? 'font-semibold text-primary' : ''}`}>
            <span className="min-w-0"><span className="mr-1.5 inline-block w-[96px] text-[11px] font-semibold uppercase tracking-wide">{i === 0 ? <><Star className="mr-1 inline h-3 w-3 fill-current" />{final ? 'Winner' : 'Leader'}</> : i === 1 ? '2nd place' : '3rd place'}</span>{t.name}<Move m={t.move} /></span><span className="shrink-0 tabular-nums">{t.votes}</span>
          </li>))}</ol>)}
      {c.others > 0 && <p className="mt-0.5 text-xs text-muted-foreground">+ {c.others} other {c.others === 1 ? 'pick' : 'picks'}</p>}
    </section>
  );
}


/** PDF version: plain body font, fixed pixel line heights and a grid per row, so wrapped lines never overlap in the captured image. */
function PdfCategory({ c, final }: { c: Cat; final: boolean }) {
  return (
    <section className="border-t-2 border-foreground pt-1.5 font-sans">
      <div className="grid grid-cols-[1fr_auto] items-start gap-3">
        <h3 className="text-[14px] font-bold leading-[19px]">{c.label}</h3>
        <span className="text-[11px] leading-[19px] text-muted-foreground">{c.ballots} votes</span>
      </div>
      {c.top.length === 0 ? <p className="text-[12px] leading-[17px] text-muted-foreground">No votes yet.</p> : (
        <div className="mt-1">{c.top.map((t, i) => (
          <div key={t.name} className={`grid grid-cols-[82px_1fr_auto] items-start gap-2 text-[12px] leading-[17px] ${i === 0 ? 'font-semibold text-primary' : ''}`}>
            <span className="text-[10px] font-semibold uppercase leading-[17px] tracking-wide">{i === 0 ? (final ? '★ Winner' : '★ Leader') : i === 1 ? '2nd place' : '3rd place'}</span>
            <span className="min-w-0 break-words">{t.name}<Move m={t.move} pdf /></span>
            <span className="tabular-nums">{t.votes}</span>
          </div>))}</div>)}
      {c.others > 0 && <p className="text-[10px] leading-[15px] text-muted-foreground">+ {c.others} other {c.others === 1 ? 'pick' : 'picks'}</p>}
    </section>
  );
}
function Header({ r }: { r: Report }) {
  return (
    <div className="relative border-b-4 border-double border-foreground pb-4 text-center">
      <img src={RHSR_LOGO} alt="Red Hook Star-Revue" crossOrigin="anonymous" className="absolute right-0 top-0 h-16 w-16 object-contain sm:h-20 sm:w-20" />
      <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-primary">Red Hook Star-Revue</div>
      <h2 className={`${head} mt-1 px-16 text-4xl font-bold sm:px-20`}>Red Hook Stars</h2>
      <p className="text-sm text-muted-foreground">Community Appreciation Awards · {isFinal(r) ? 'Final reader ballot results' : 'Current standings — voting closes November 10, 2026'}</p>
      <p className="mt-2 text-sm"><strong>{r.total}</strong> ballots · prepared {new Date(r.generated).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</p>
      <p className="mt-1 text-xs text-muted-foreground"><span className="font-bold text-[hsl(var(--brand-green))]">▲</span> moved up · <span className="font-bold text-primary">▼</span> moved down, compared with standings before {new Date(`${r.compareTo}T12:00:00`).toLocaleDateString("en-US", { month: "long", day: "numeric" })} ({r.prevTotal} ballots)</p>
    </div>
  );
}

function WriteIns({ items, total }: { items: string[]; total: number }) {
  return (
    <section className="break-inside-avoid border-t-2 border-foreground pt-2">
      <h3 className={`${head} text-lg font-semibold`}>"Did we miss a category?" ({total})</h3>
      {items.length === 0 ? <p className="text-sm text-muted-foreground">No suggestions.</p> :
        <ul className="mt-1 list-disc space-y-0.5 pl-5 text-sm">{items.map((w) => <li key={w}>{w}</li>)}</ul>}
    </section>
  );
}

const NOTE = 'Spelling variants are combined (e.g. "Hook\'d on Pets" and "Hooked on pets"). Blank, "n/a" and "none" answers are not counted.';

/** On-screen report. */
function Doc({ r }: { r: Report }) {
  const final = isFinal(r);
  return (
    <div className="bg-[hsl(var(--paper))] p-6 text-foreground sm:p-10">
      <Header r={r} />
      <div className="mt-6 grid gap-x-8 gap-y-6 md:grid-cols-2">{r.categories.map((c) => <Category key={c.label} c={c} final={final} />)}</div>
      <div className="mt-8"><WriteIns items={r.writeIns} total={r.writeIns.length} /></div>
      <p className="mt-6 text-xs text-muted-foreground">{NOTE}</p>
    </div>
  );
}

/** PDF: each child is exactly one Letter page (600 × 776), so type stays full size. */
const PER_PAGE = 6;
function PdfPages({ r, refs }: { r: Report; refs: MutableRefObject<(HTMLDivElement | null)[]> }) {
  const final = isFinal(r);
  const cats: Cat[][] = [];
  for (let i = 0; i < r.categories.length; i += PER_PAGE) cats.push(r.categories.slice(i, i + PER_PAGE));
  const wi: string[][] = [];
  for (let i = 0; i < r.writeIns.length; i += 26) wi.push(r.writeIns.slice(i, i + 26));
  const pages: ReactNode[] = [
    <><Header r={r} /><div className="mt-5"><StarsDaily r={r} forPdf /></div></>,
    ...cats.map((group) => <div key={group[0]?.label} className="space-y-3">{group.map((c) => <PdfCategory key={c.label} c={c} final={final} />)}</div>),
    ...(wi.length ? wi : [[]]).map((items, i) => <><WriteIns items={items} total={r.writeIns.length} />{i === Math.max(wi.length, 1) - 1 && <p className="mt-6 text-xs text-muted-foreground">{NOTE}</p>}</>),
  ];
  const n = pages.length;
  return (
    <>{pages.map((p, i) => (
      <div key={`page-${i + 1}`} ref={(el) => { refs.current[i] = el; }} className="relative h-[776px] w-[600px] overflow-hidden bg-[hsl(var(--paper))] p-8 text-foreground">
        {p}
        <div className="absolute bottom-4 left-8 right-8 flex justify-between border-t border-border pt-1 text-[10px] text-muted-foreground"><span>Red Hook Stars · Red Hook Star-Revue</span><span>Page {i + 1} of {n}</span></div>
      </div>
    ))}</>
  );
}

export function StarsReport() {
  const [r, setR] = useState<Report | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const [compareTo, setCompareTo] = useState<string | undefined>(undefined);
  const pageRefs = useRef<(HTMLDivElement | null)[]>([]);
  useEffect(() => {
    let on = true;
    setLoading(true);
    getStarsReport({ data: { pass: getStaffPass(), compareTo } }).then((x) => { if (on) setR(x ?? null); })
      .catch((e) => { console.error(e); if (on) setError('The Red Hook Stars results could not be loaded. Please try again.'); })
      .finally(() => { if (on) setLoading(false); });
    return () => { on = false; };
  }, [compareTo]);
  const savePdf = async () => {
    setExporting(true);
    try { await exportPagesToPdf(pageRefs.current.filter((x): x is HTMLDivElement => !!x), 'red-hook-stars-results.pdf'); }
    catch (e) { console.error(e); setError('The PDF could not be created. Please try again.'); }
    finally { setExporting(false); }
  };
  const btn = 'inline-flex min-h-11 items-center gap-2 border border-foreground px-4 text-sm font-semibold disabled:opacity-50';
  return (
    <section>
      <div className="flex flex-wrap items-end justify-between gap-3 print:hidden">
        <div><h2 className={`${head} text-3xl font-semibold`}>Red Hook Stars report</h2><p className="text-sm text-muted-foreground">The top pick and next two in every category (shown as "Leader" until voting closes November 10), read live from the ballot sheet.</p></div>
        {r && <div className="flex gap-2">
          <button className={btn} disabled={exporting} onClick={() => void savePdf()}>{exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileDown className="h-4 w-4" />}Save PDF</button>
          <button className={btn} onClick={() => window.print()}><Printer className="h-4 w-4" />Print</button>
        </div>}
      </div>
      {loading && !r && <div className="mt-6 grid gap-4 md:grid-cols-2">{[0, 1, 2, 3].map((k) => <div key={k} className="h-32 animate-pulse bg-muted" />)}</div>}
      {error && <p role="alert" className="mt-6 text-sm text-destructive">{error}</p>}
      {r && (
        <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 border border-border bg-card px-4 py-3 text-sm print:hidden">
          <label htmlFor="stars-compare" className="font-semibold">Compare with standings before</label>
          <input id="stars-compare" type="date" value={r.compareTo} onChange={(e) => e.target.value && setCompareTo(e.target.value)} className="h-11 border border-input bg-background px-2" />
          <span className="text-muted-foreground">({r.prevTotal} ballots then, {r.total} now)</span>
          <span className="inline-flex items-center gap-1"><ArrowUp strokeWidth={3} className="h-4 w-4 text-[hsl(var(--brand-green))]" />moved up</span>
          <span className="inline-flex items-center gap-1"><ArrowDown strokeWidth={3} className="h-4 w-4 text-primary" />moved down</span>
          {loading && <Loader2 className="h-4 w-4 animate-spin" />}
        </div>
      )}
      {r && <StarsDaily r={r} />}
      {r && <div className="mt-6 border border-border"><Doc r={r} /></div>}
      {r && <StarsBallot r={r} />}
      {r && <StarsSocial r={r} final={isFinal(r)} />}
      {r && <div aria-hidden className="pointer-events-none fixed left-[-10000px] top-0 space-y-4"><PdfPages r={r} refs={pageRefs} /></div>}
    </section>
  );
}
